import React from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

export const HelpDialog = ({ isOpen, onClose }) => {
  const faqData = [
    {
      id: 1,
      question: "What is the flow of creating visit?",
      answer: (
        <div className="space-y-3">
          <p className="text-gray-700">Creating a visit follows these steps:</p>
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Navigate to Visits tab</strong> - Click on the "Visits" tab in the main navigation</li>
            <li><strong>Click "Add Visit"</strong> - Use the blue "Add Visit" button in the Visits section</li>
            <li><strong>Fill out the form:</strong>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>PI Name - Principal Investigator name</li>
                <li>Site - Select from dropdown (with search functionality)</li>
                <li>Study - Select from related studies (auto-populates based on site)</li>
                <li>Visit Type - Choose: SIV, Screening, Baseline, Follow-up, etc.</li>
                <li>Patient Name - Subject identifier</li>
                <li>Contact - Email or phone</li>
                <li>Date & Time - Schedule the visit</li>
                <li>Status - Usually "Ongoing" for new visits</li>
                <li>Comments - Any additional notes</li>
              </ul>
            </li>
            <li><strong>Submit</strong> - The system automatically generates tasks based on visit type</li>
            <li><strong>Review generated tasks</strong> - Check Dashboard for auto-created tasks with proper coordinator assignments</li>
          </ol>
        </div>
      )
    },
    {
      id: 2,
      question: "How can I create a study startup?",
      answer: (
        <div className="space-y-3">
          <p className="text-gray-700">Study startup involves applying the Study Startup template:</p>
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Go to Dashboard</strong> - Main task management view</li>
            <li><strong>Click "Apply Template"</strong> - Blue button in the top controls</li>
            <li><strong>Select "Study Startup Template"</strong> - Choose from dropdown</li>
            <li><strong>Fill out template form:</strong>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Study - Select the study (auto-populates related sites)</li>
                <li>Site - Choose appropriate site (auto-populates coordinator)</li>
                <li>Day 0 Date - Set the SIV (Site Initiation Visit) date</li>
                <li>Visit - Optional visit information</li>
              </ul>
            </li>
            <li><strong>Apply Template</strong> - Creates 25+ tasks including:
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Regulatory binder creation</li>
                <li>Site setup and activation</li>
                <li>Equipment calibration</li>
                <li>Staff training and delegation</li>
                <li>Study checklist completion</li>
              </ul>
            </li>
            <li><strong>Monitor Progress</strong> - Track tasks assigned to coordinators</li>
          </ol>
        </div>
      )
    },
    {
      id: 3,
      question: "How to create a new study?",
      answer: (
        <div className="space-y-3">
          <p className="text-gray-700">Creating a new study is done through the Admin panel:</p>
          <ol className="list-decimal pl-6 space-y-2">
            <li><strong>Navigate to Admin</strong> - Click "Admin" tab in main navigation</li>
            <li><strong>Go to Studies section</strong> - Click on "Studies" in the admin panel</li>
            <li><strong>Click "Add Study"</strong> - Green plus button to create new study</li>
            <li><strong>Fill out study information:</strong>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Name</strong> - Study title (required)</li>
                <li><strong>Protocol Number</strong> - Study protocol identifier</li>
                <li><strong>Sponsor</strong> - Pharmaceutical company or organization</li>
                <li><strong>Indication</strong> - Medical condition being studied</li>
                <li><strong>Sites</strong> - Associate participating sites</li>
                <li><strong>Status</strong> - Usually "Active" for new studies</li>
              </ul>
            </li>
            <li><strong>Save Study</strong> - Study becomes available for visit creation and task templates</li>
            <li><strong>Associate Sites</strong> - Link relevant clinical sites that will participate</li>
          </ol>
        </div>
      )
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="help-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <HelpCircle className="h-6 w-6 text-blue-600" />
            StudyTracker Pro - Help & FAQ
          </DialogTitle>
          <DialogDescription>
            Get help with common tasks and workflows in StudyTracker Pro
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {faqData.map((faq) => (
            <div key={faq.id} className="border rounded-lg p-4 bg-gray-50">
              <h3 className="font-semibold text-lg text-gray-900 mb-3 flex items-start gap-2">
                <span className="bg-blue-600 text-white text-sm rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  {faq.id}
                </span>
                {faq.question}
              </h3>
              <div className="ml-8">
                {faq.answer}
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={() => onClose(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
